import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   computed,
   inject,
   Injector,
   OnDestroy,
   signal,
   Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectOpenDialogEvent,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectStateInitEventPayload,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { PhoneNumberOption } from 'src/app/common-components/entity-select/phonenumber/models/phone-number-option';
import { phoneNumberOptionActions } from 'src/app/common-components/entity-select/phonenumber/state/phone-number-option.actions';
import { phoneNumberOptionSelectors } from 'src/app/common-components/entity-select/phonenumber/state/phone-number-option.reducer';
import { formatPhoneNumbers } from 'src/app/pipes/format-phone-numbers/format-phone-numbers.pipe';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ReportParameterComponent } from '../../report-parameter-list/report-parameter-component';
import { ReportParameterConfig } from '../../report-parameter-list/report-parameter.config';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: PhoneNumbersReportParameterComponent }],
    selector: 'ndt-phone-numbers-report-parameter',
    templateUrl: './phone-numbers-report-parameter.component.html',
    styleUrls: ['./phone-number-report-parameter.component.scss'],
    standalone: false
})
export class PhoneNumbersReportParameterComponent implements OnDestroy, ReportParameterComponent {
   form: UntypedFormGroup;

   selectedAccountId: number | null;

   searchControl = new UntypedFormControl('');

   private destroyed$ = new Subject<void>();

   config: ReportParameterConfig;

   phonePanelDisabled: boolean;

   entityName = 'Phone Numbers';

   readonly store = inject(Store);
   readonly cd = inject(ChangeDetectorRef);
   readonly injector = inject(Injector);

   readonly allPhoneNumbers = this.store.selectSignal(phoneNumberOptionSelectors.selectAll);
   readonly phoneNumbers = this.store.selectSignal(phoneNumberOptionSelectors.selectFilteredItems);
   readonly phoneNumbersCallState = this.store.selectSignal(phoneNumberOptionSelectors.selectCallState);

   // Store toSignal results outside of computed
   private _phoneNumberItemsSignal = signal<Signal<PhoneNumberOption[]> | null>(null);
   private _allSignal = signal<Signal<boolean> | null>(null);

   readonly phoneNumberItemsFromControl = computed(
      () => {
         const sig = this._phoneNumberItemsSignal();
         return sig ? sig() : [];
      },
      { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
   );

   readonly allFromControl = computed(() => {
      const sig = this._allSignal();
      return sig ? sig() : false;
   });

   private updatePhoneNumberItemsSignal() {
      const phoneNumberItemsControl = this.form?.controls?.phoneNumbers;
      if (phoneNumberItemsControl) {
         const sig = toSignal<PhoneNumberOption[]>(
            phoneNumberItemsControl.valueChanges.pipe(map(() => phoneNumberItemsControl.getRawValue() ?? [])),
            {
               initialValue: phoneNumberItemsControl.getRawValue() ?? [],
               injector: this.injector,
            }
         ) as Signal<PhoneNumberOption[]>;
         this._phoneNumberItemsSignal.set(sig);
      } else {
         this._phoneNumberItemsSignal.set(null);
      }
   }

   private updateAllSignal() {
      const allControl = this.form?.controls?.all;
      if (allControl) {
         const sig = toSignal(allControl.valueChanges.pipe(map(() => allControl.getRawValue() ?? false)), {
            initialValue: allControl.getRawValue() ?? false,
            injector: this.injector,
         });
         this._allSignal.set(sig);
      } else {
         this._allSignal.set(null);
      }
   }

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<PhoneNumberOption>>(() => {
      return {
         displayOptions: {
            label: this.entityName,
            displayType: LcsSelectWithAllDisplayTypes.Label,
            displayWithFn: (
               items: PhoneNumberOption[],
               state: LcsAdvancedMultiSelectReadonlyStateType<PhoneNumberOption>
            ) => {
               const options = this.phoneNumberItemsFromControl() ?? [];
               if (options.length === 1) {
                  return formatPhoneNumbers(options[0].phoneNumber);
               }
               return `${options.length} ${this.entityName}`;
            },
            hasAllCheckbox: true,
         },
         group: new LcsAdvancedMultiSelectTriggerFormGroup<PhoneNumberOption>(
            {
               items: new FormControl(this.phoneNumberItemsFromControl() ?? [], { nonNullable: true }),
               all: new FormControl(this.allFromControl(), { nonNullable: true }),
            },
            {
               validators: this.form.hasValidator(Validators.required) ? [Validators.required] : [],
            }
         ),
         syncOn: 'save',
      };
   });

   readonly selectedPhoneNumbers = computed(() => {
      return signal(this.phoneNumberItemsFromControl());
   });

   readonly dataSource = computed<LcsAdvancedMultiSelectDatasource<PhoneNumberOption>>(() => {
      return {
         id: 1,
         label: this.entityName,
         items: this.availablePhoneNumbers() ?? [],
         selectedItems: this.selectedPhoneNumbers()(),
         itemTrackBy: this.phoneNumberTrackBy,
         displayOptions: {
            displayWithFn: (item: PhoneNumberOption) => {
               return item.phoneNumber ?? '';
            },
         },
         callState: this.phoneNumbersCallState(),
      };
   });

   readonly availablePhoneNumbers = computed(() => {
      const phoneNumbers = this.phoneNumbers();
      return phoneNumbers;
   });

   constructor() {
      this.store
         .select(getSelectedAccountId)
         .pipe(takeUntil(this.destroyed$))
         .subscribe((selectedAccountId) => {
            if (selectedAccountId) {
               this.selectedAccountId = selectedAccountId;
               this.form?.controls?.accountId?.setValue(this.selectedAccountId);
               this.store.dispatch(
                  phoneNumberOptionActions.load({
                     additionalFields: [],
                     filterOverrides: [`accountId = ${selectedAccountId}`],
                  })
               );
            }
         });
   }

   initializeSignals() {
      this.updatePhoneNumberItemsSignal();
      this.updateAllSignal();
      setTimeout(() => this.cd.markForCheck(), 0); //Force form to display required * on phoneNumbers control
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onSelectAll(isChecked: boolean, panel: any) {
      if (isChecked) {
         this.phonePanelDisabled = true;
         this.form.get('phoneNumbers')?.disable();
         panel.selectAll();
      } else {
         this.phonePanelDisabled = false;
         this.form.get('phoneNumbers')?.enable();
         panel.deselectAll();
      }

      this.form.controls.phoneNumbers?.updateValueAndValidity();
   }

   onAllChange(all: boolean) {
      this.form.controls.all.setValue(all);
   }

   onInit(e: LcsAdvancedMultiSelectStateInitEventPayload<PhoneNumberOption>) {
      this.initializeSignals();

      this.store.dispatch(
         phoneNumberOptionActions.load({
            additionalFields: [],
            filterOverrides: [`accountId = ${this.selectedAccountId}`],
         })
      );
      this.form?.controls?.accountId?.setValue(this.selectedAccountId);
   }

   onOpen(e: LcsAdvancedMultiSelectOpenDialogEvent<PhoneNumberOption>) {
      this.store.dispatch(phoneNumberOptionActions.searchReset());
   }

   onSave(e: LcsAdvancedMultiSelectStateEventPayloads<PhoneNumberOption>['save']) {
      const selectedItems = e.state.selectedItems();
      this.form.controls.phoneNumbers.setValue(selectedItems);
      this.triggerConfig().group?.markAsDirty();
      this.triggerConfig().group?.markAsTouched();
   }

   onSearch(e: LcsAdvancedMultiSelectStateEventPayloads<PhoneNumberOption>['search']) {
      const searchText = e.searchText.replace(/[()-]/g, '');
      this.store.dispatch(phoneNumberOptionActions.search({ searchTerm: searchText }));
   }

   onSelectedItemsChange(e: LcsAdvancedMultiSelectStateEventPayloads<PhoneNumberOption>['selectedItemsChange']) {
      this.selectedPhoneNumbers().set(e.state.selectedItems() as PhoneNumberOption[]);
   }

   onClose(e: LcsAdvancedMultiSelectStateEventPayloads<PhoneNumberOption>['close']) {
      const datasource = this.dataSource();
      if (datasource) {
         e.actions.datasourceChange({ datasource });
      }
      this.selectedPhoneNumbers().set(this.phoneNumberItemsFromControl());
   }

   isPhoneNumberSelected(selectedPhoneNumbers: PhoneNumberOption[], phoneNumber: PhoneNumberOption) {
      return selectedPhoneNumbers.some((p) => this.phoneNumberTrackBy(p) === this.phoneNumberTrackBy(phoneNumber));
   }

   private phoneNumberTrackBy = (phoneNumber: PhoneNumberOption) => phoneNumber.phoneNumberId;
}
